import { mapValues } from 'lodash';
import * as React from 'react';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ReactOnRails from 'react-on-rails';
import type { RenderFunction } from 'react-on-rails/node_package/lib/types';
import { hash } from 'spark-md5';
import { ICompanySettings } from '../javascript/application/useCompanySettings';
import { FeatureFlags } from '../javascript/application/useFeatureFlags';
import { OnboardingSettings } from '../javascript/application/useOnboardingSettings';
import { debugChunkLoadingError } from './debugChunkLoadingError';
import { GlobalProviderWrapper } from './shared/GlobalProviderWrapper';
import { TEnabledLocaleKeys } from './shared/hooks/useLocaleContext';
import { reportError } from './shared/utilities/reportError';

// the chunk loading error is a special case - the user might be able to do something about it.
function isChunkLoadingError(error: Error): boolean {
  // note that in React development mode, the error messages are different to production, so this won't work.
  return error.name === 'ChunkLoadError';
}

// we can take the error message and make a hash that will be displayed in the fallback
function generateCorrelation(error: Error) {
  if (!error.message) return null;

  const errorHash = hash(error.message).substring(0, 8);
  return errorHash;
}

function SuspenseFallback() {
  return <div className="react-suspense-fallback" />;
}

function ErrorFallback({ error }: { error: Error }) {
  const correlation = generateCorrelation(error);

  // show a slightly different message for chunk loading errors
  if (isChunkLoadingError(error)) {
    // now, even though this is in the render loop, we _should_ be ok, as this component should only be rendered once
    // We are being completely swamped by these errors in rollbar so we are stopping them for the moment
    // debugChunkLoadingError(error);

    return (
      <div className="react-error-fallback">
        <h2 className="bp3-heading">Unable to load</h2>
        <div className="bp3-text-muted bp3-caption">
          This component could not load correctly - this may be caused by an ad blocker, slow network connection, or
          corporate firewall.
          <br />
          Please disable your ad blocker on this site, or try a different browser or device. If the problem persists,
          please contact support. {correlation && <span style={{ opacity: 0.3 }}>Reference: {correlation}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="react-error-fallback">
      <h2 className="bp3-heading">Sorry, looks like something went wrong</h2>
      <div className="bp3-text-muted bp3-caption">
        If the problem persists, please contact support.{' '}
        {correlation && <span style={{ opacity: 0.3 }}>Reference: {correlation}</span>}
      </div>
    </div>
  );
}

function reportReactComponentError(error: Error) {
  // We aren't reporting chunk load errors to rollbar to reduce the noise we are seeing in there
  //
  if (isChunkLoadingError(error)) return null;

  const correlation = generateCorrelation(error);
  reportError(error, { errorFallbackRendered: true, correlation });
}

function wrapComponentInRenderFunction(LoadedComponent: React.ElementType<any>) {
  const renderFunction: RenderFunction = (props, railsContext) => {
    const { companySettings, enabledLocaleKeys, featureFlags, onboardingSettings, ...rest } = props;
    // https://www.shakacode.com/react-on-rails/docs/guides/render-functions-and-railscontext/
    const noArgumentsRenderFunction = () => {
      return (
        <GlobalProviderWrapper
          companySettings={companySettings as ICompanySettings}
          enabledLocaleKeys={enabledLocaleKeys as TEnabledLocaleKeys}
          featureFlags={featureFlags as FeatureFlags}
          onboardingSettings={onboardingSettings as OnboardingSettings}
        >
          <Suspense fallback={<SuspenseFallback />}>
            <ErrorBoundary FallbackComponent={ErrorFallback} onError={reportReactComponentError}>
              <LoadedComponent
                {...rest}
                // TODO: TOTIDY remove the below once we know that no sub-contexts are using them also
                // eg. when we have removed all the providers throughout the components
                companySettings={companySettings}
                enabledLocaleKeys={enabledLocaleKeys}
                featureFlags={featureFlags}
              />
            </ErrorBoundary>
          </Suspense>
        </GlobalProviderWrapper>
      );
    };
    return noArgumentsRenderFunction;
  };
  return renderFunction;
}

interface IRegisterReactComponents {
  [id: string]: React.ElementType<any>;
}

export function registerReactComponents(components: IRegisterReactComponents) {
  const wrappedComponents = mapValues(components, (component) => wrapComponentInRenderFunction(component));
  ReactOnRails.register(wrappedComponents);
}
